"use client"

import { buildExcerpt } from '@/lib/blog-excerpt'

import Link from "next/link"
import { CardImage } from "@/components/brand-kit/card-image"
import { alternateCardImage } from "@/lib/card-image"
import { Breadcrumb } from "@/components/brand-kit/breadcrumb"
import { hubBreadcrumbs } from "@/lib/breadcrumbs"
import { ArticleCard } from "@/components/brand-kit/article-card"
import { ToolLinkCard } from "@/components/brand-kit/tool-link-card"
import { FAQAccordion } from "@/components/brand-kit/faq-accordion"
import { ProductCarouselCard } from "@/components/brand-kit/product-carousel-card"
import { ProductShareButtons } from "@/components/blocks/product-share-buttons"
import { BrandLogoGrid } from "@/components/brand-kit/brand-logo-grid"
import {
  PAGE_W,
  transformProducts, toCarouselProduct, getProductUrl,
  getFeaturedProducts, getTopRatedProducts, getBestValueProducts,
  formatPrice, formatDate, formatProductTitle, stripBestPrefix,
  PageSectionHeader, ScrollCarousel, StarRating,
  hubHeroIntro,
  type HubClientProps,
  hubCollectionHref,
  hubBrandHref,
  MaybeLink,
} from "./hub-shared"
import { productCardCtaName } from "@/lib/card-link-names"
import { dealCtaLabel, hasRenderedPrice } from "@/lib/deal-cta"
import { OptimizedImg } from "@/components/ui/optimized-img"

export default function BrandHub({ hub, products, articles, relatedHubs = [], brandPeers = [] }: HubClientProps) {
  const collectionSlug = hub.collection_slug || hub.slug
  const collectionHref = hubCollectionHref(hub) // FRIDGE-1119: null when the collection does not render
  const displayProducts = transformProducts(products)
  const popularProducts = getFeaturedProducts(displayProducts)
  const topRatedProducts = getTopRatedProducts(displayProducts)
  const bestValueProducts = getBestValueProducts(displayProducts)

  // Extract brand name from title (e.g. "Samsung Refrigerators" -> "Samsung")
  const brandName = hub.title.replace(/\s*(Refrigerators|Freezers|Fridges|Appliances)$/i, '').trim()

  // Get brand logo from brands_section (enriched in server component)
  const brandLogo = hub.brands_section?.[0]?.logo

  return (
    <div>
      <div className={`${PAGE_W} py-8`}>
        {/* ONE trail (lib/breadcrumbs.ts) — page.tsx emits the same hubBreadcrumbs(hub) as the BreadcrumbList (FRIDGE-269) */}
        <Breadcrumb trail={hubBreadcrumbs(hub)} />

        {/* Hero Banner — Brand Spotlight with logo */}
        <section className="mb-12 rounded-2xl overflow-hidden border border-gray-100 bg-gradient-to-br from-white to-gray-50 shadow-[0_4px_24px_rgba(0,25,69,0.06)]">
          <div className="py-12 px-8 lg:px-12">
            {brandLogo ? (
              <img src={brandLogo} alt={`${brandName} logo`} className="h-10 object-contain mb-4" />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-[#0469f0]/10 flex items-center justify-center mb-4">
                <span className="text-2xl font-black text-[#0469f0]">{brandName.charAt(0)}</span>
              </div>
            )}
            <span className="text-[#ff9951] font-bold tracking-widest text-xs mb-3 block uppercase">Brand Spotlight</span>
            <h1 className="text-3xl md:text-4xl font-black tracking-tighter leading-tight text-gray-900 mb-3">{hub.title}</h1>
            {/* First sentence — keyword-placement standard (plan item 15).
                Always renders (fallback leads with hub.title). */}
            <p className="text-gray-500 text-sm leading-relaxed mb-6 max-w-lg">{hubHeroIntro(hub)}</p>
            {collectionHref ? (<Link
              href={collectionHref}
              className="inline-flex items-center gap-2 bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
            >
              Browse All {brandName} Products
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </Link>) : null}
          </div>
        </section>

        <div className="mb-6">
          <ProductShareButtons productTitle={hub.title} pageType="guide" variant="top" />
        </div>

        {/* ─── Featured Products Grid ─────────────────────────────────── */}
        {displayProducts.length > 0 && (
          <section className="mb-12">
            <PageSectionHeader
              eyebrow={`${brandName} Products`}
              title={`Top ${stripBestPrefix(brandName)} Models`}
              actions={
                collectionHref ? (<Link href={collectionHref} className="tap-24 text-sm font-medium text-[#0469f0] hover:underline flex items-center gap-1">
                  View All
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </Link>) : undefined
              }
            />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {displayProducts.slice(0, 8).map((product, idx) => {
                const productUrl = getProductUrl(product)
                // FRIDGE-429 — ONE boolean gates the price row AND picks the CTA
                // label, read from the very column the price element renders.
                const showsPrice = hasRenderedPrice(product.lowest_price_cents)
                return (
                  <div key={product.id} className="group bg-white rounded-xl border border-gray-100 hover:border-[#0469f0]/20 hover:shadow-[0_4px_20px_rgba(0,25,69,0.06)] transition-all overflow-hidden flex flex-col">
                    <div className="relative">
                      <div className="aspect-square bg-white overflow-hidden">
                        <CardImage src={product.images[0] || ''} alternate={alternateCardImage(product.images[0] || '', product.images)} href={productUrl} alt={`${product.title} | Fridge.com`} width={300} height={300} priority={idx < 3} />
                      </div>
                    </div>
                    <div className="p-4 border-t border-gray-50 flex flex-col flex-grow">
                      <Link href={productUrl}>
                        <h3 className="text-sm font-semibold text-gray-900 leading-snug mb-2 line-clamp-3 group-hover:text-[#0469f0] transition-colors">{formatProductTitle(product.title)}</h3>
                      </Link>
                      {showsPrice && (
                        <div className="flex flex-wrap items-baseline gap-x-2 mb-1">
                          <span className="text-lg font-black text-gray-900">{formatPrice(product.lowest_price_cents)}</span>
                          {product.originalPrice > product.price && (
                            <span className="text-xs text-gray-600 line-through">{formatPrice(product.average_price_30d_cents)}</span>
                          )}
                        </div>
                      )}
                      <div className="mb-3">
                        <StarRating rating={product.rating} />
                      </div>
                      <Link href={productUrl} aria-label={productCardCtaName(dealCtaLabel(showsPrice), product, productUrl)} className="mt-auto w-full bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] text-sm font-bold py-2.5 rounded-lg transition-all active:scale-95 flex items-center justify-center gap-2">
                        {dealCtaLabel(showsPrice)}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7H7M17 7v10" /></svg>
                      </Link>
                    </div>
                  </div>
                )
              })}
            </div>
          </section>
        )}

        {/* ─── Explore Related Categories ─────────────────────────────── */}
        {relatedHubs.length > 0 && (
          <section className="mb-12">
            <PageSectionHeader eyebrow="Discover More" title="Explore Related Categories" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {relatedHubs.slice(0, 5).map((rh) => (
                <ToolLinkCard key={rh.slug} title={rh.title} description="Browse collection" href={`/${rh.slug}`} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* ─── Carousel 1: Featured (FRIDGE-1485) ────────────────────────────────── */}
      {popularProducts.length > 0 && (
        <ScrollCarousel eyebrow="Featured Models" title={`Featured ${stripBestPrefix(brandName)} Models`} bgClass="bg-white">
          {popularProducts.map((p) => (
            <ProductCarouselCard key={p.id} product={toCarouselProduct(p)} />
          ))}
        </ScrollCarousel>
      )}

      {/* ─── Carousel 2: Top-Rated ──────────────────────────────────── */}
      {topRatedProducts.length > 0 && (
        <ScrollCarousel eyebrow="Highest Rated" title={`Top-Rated from ${stripBestPrefix(brandName)}`} bgClass="bg-white">
          {topRatedProducts.map((p) => (
            <ProductCarouselCard key={p.id} product={toCarouselProduct(p)} />
          ))}
        </ScrollCarousel>
      )}

      {/* ─── FAQ ──────────────────────────────────────────────────────── */}
      {hub.faqs && hub.faqs.length > 0 && (
        <div className={`${PAGE_W} py-8`}>
          <PageSectionHeader eyebrow="Common Questions" title={`${stripBestPrefix(brandName)} FAQ`} />
          <FAQAccordion items={hub.faqs} defaultOpen={[0]} columns={1} title="" bordered={false} className="mt-0" />
        </div>
      )}

      {/* ─── Carousel 3: Best Value ──────────────────────────────────── */}
      {bestValueProducts.length > 0 && (
        <ScrollCarousel eyebrow="Best Deals" title={`Best Value from ${stripBestPrefix(brandName)}`} bgClass="bg-white">
          {bestValueProducts.map((p) => (
            <ProductCarouselCard key={p.id} product={toCarouselProduct(p)} />
          ))}
        </ScrollCarousel>
      )}

      <div className={`${PAGE_W} py-4`}>
        <ProductShareButtons productTitle={hub.title} pageType="guide" variant="bottom" />
      </div>

      <div className={PAGE_W}>
        {/* No brands section — this IS a brand page */}

        {articles.length > 0 && (
          <section className="mb-12">
            <PageSectionHeader
              eyebrow="Expert Insights"
              title="Latest from the Fridge.com Blog"
              actions={
                <Link href="/blogs/news" className="tap-24 text-sm font-medium text-[#0469f0] hover:underline flex items-center gap-1">
                  All Articles
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </Link>
              }
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {articles.map((article) => (
                <ArticleCard key={article.id} imageUrl={article.image || undefined} title={article.title} author={article.author_name || "Fridge.com"} authorImageUrl={article.authorImageUrl ?? null} date={formatDate(article.published_at)} excerpt={buildExcerpt(article.content || '', article.title)} href={`/blogs/news/${article.slug}`} />
              ))}
            </div>
          </section>
        )}

        {hub.tools_section && hub.tools_section.length > 0 && (
          <section className="mb-12">
            <PageSectionHeader eyebrow="Interactive Tools" title="Helpful Tools" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {hub.tools_section.map((tool) => (
                <ToolLinkCard key={tool.href} title={tool.title} description={tool.description} href={tool.href} />
              ))}
            </div>
          </section>
        )}

        {hub.cta_title && (
          <section className="relative rounded-2xl overflow-hidden mb-12">
            <OptimizedImg src="https://images.unsplash.com/photo-1541604193435-22287d32c2c2?w=1200&q=80" alt="" fill sizes="(min-width: 1280px) 1216px, 100vw" className="object-cover" />
            <div className="absolute inset-0 bg-black/65" />
            <div className="relative z-10 text-center py-16 px-10">
              <h2 className="text-2xl md:text-3xl font-black tracking-tight text-white mb-3 drop-shadow-lg">{hub.cta_title}</h2>
              {hub.cta_description && <p className="text-white/80 text-sm mb-6 max-w-xl mx-auto">{hub.cta_description}</p>}
              {(hub.cta_button_link || collectionHref) ? (<Link href={(hub.cta_button_link || collectionHref) as string} className="inline-flex items-center gap-2 bg-[#ff9951] hover:bg-[#f08030] text-[var(--color-brand-action-text)] font-semibold px-7 py-3 rounded-lg transition-colors text-sm shadow-lg">
                {hub.cta_button_text || `Browse All ${brandName} Products`}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </Link>) : null}
            </div>
          </section>
        )}

        {/* ─── Explore Other Brands — BrandLogoGrid ──────────────────── */}
        {brandPeers && brandPeers.length > 0 && (
          <div className="mb-16">
            <BrandLogoGrid
              brands={brandPeers}
              title="Explore Other Brands"
              description="Compare top refrigerator manufacturers on Fridge.com."
              initialCount={10}
            />
          </div>
        )}
      </div>
    </div>
  )
}
